<template>
  <section class="property-navigation pt-8">
    <button
        v-for="item in items" :key="item.label"
        class="text-body-1 fw-semi-bold"
        :class="{selected: item.code === selectedItemCode, 'desktop-only': item.desktopOnly}"
        :aria-label="item.label"
        @click="select(item)"
    >
      {{$t(item.label)}}
    </button>
  </section>
</template>
<script setup lang="ts">

import {scrollToElement, throttle} from '~/utils/common'

interface  Item {
  code: string,
  label: string,
  selector: string,
  desktopOnly: boolean
}

const items:Item[] = [
  {code: 'about', label: 'About', selector: '.property-info__list', desktopOnly: false},
  {code: 'rooms', label: 'Rooms', selector: '#propertyRooms', desktopOnly: false},
  {code: 'facilities', label: 'Facilities', selector: '#PropertyFacilities', desktopOnly: false},
  {code: 'policies', label: 'Policies', selector: '#PropertyPolicies', desktopOnly: false},
  {code: 'location', label: 'Location', selector: '#PropertyLocation', desktopOnly: false},
]
let sectionsElements = [] as any[]

let selectedItemCode = ref(items[0]?.code as string)

function select(item: Item) {
  if (item.selector) {
    scrollToElement(item.selector, -100)
  }
}

function getSectionsElements() {
  sectionsElements = []
  items.forEach(item => {
    sectionsElements.push({
      code: item.code,
      element: document.querySelector(item.selector)
    })
  })
}

function scrollListener() {
  let selectedCode
  let isElementInList = false

  for(let i = 0; i < sectionsElements.length; i++) {
    isElementInList = true
    const elPosition = sectionsElements[i].element.offsetTop
    if ((window.scrollY + 120) < elPosition) {
      break;
    }

    selectedCode = sectionsElements[i].code

    if (selectedCode === 'location') {
      isElementInList = window.scrollY - 120 < elPosition
    }
  }
  selectedItemCode.value = selectedCode || items[0]?.code

  togglePropertyNavigationPosition(isElementInList)
}

function togglePropertyNavigationPosition(isElementInList: boolean) {
  const _propertyNavigationEl = document?.querySelector('.property-navigation')
  if (!_propertyNavigationEl) return;

  if (!isElementInList) {
    _propertyNavigationEl?.setAttribute('style', 'position: unset')
  } else {
    _propertyNavigationEl?.removeAttribute('style')
  }
}

const scrollListenerThrottle = throttle(scrollListener, 100)

onMounted(() => {
  getSectionsElements()
  window.addEventListener('scroll', scrollListenerThrottle);
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scrollListenerThrottle);
})

</script>

<style lang="scss" scoped>
.property-navigation {
  display: flex;
  width: 100%;
  max-width: calc(100% - 357px - 24px);
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: var(--white);
  padding-bottom: 8px;

  button {
    flex: 1;
    max-width: 148px;
    height: 44px;
    padding-bottom: 16px;
    border-bottom: 2px solid var(--grey-border);

    @media screen and (max-width: 768px) {
      height: 38px;
    }

    @media screen and (max-width: 430px) {
      min-width: 33.3vw;
    }

    &.selected {
      padding-bottom: 15px;
      border-bottom: 3px solid var(--blue);
      color: var(--blue);

      @media screen and (max-width: 430px) {
        border-bottom-width: 2px;
      }
    }
  }

  // Tablet two-column: constrain width to content column
  @media screen and (max-width: 1024px) and (min-width: 769px) {
    max-width: 608px;
    button {
      flex: 1;
      max-width: 100%;
    }
  }

  // Single-column: full width with horizontal scroll
  @media screen and (max-width: 768px) {
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    button {
      flex: 1;
      max-width: 100%;
    }
  }

  @media screen and (max-width: 430px) {
    $fullLengthWidth: 100vw;
    max-width: $fullLengthWidth;
    min-width: $fullLengthWidth;
    margin-left: -16px;
  }

  &.pt-8 {

    @media screen and (max-width: 430px) {
      padding-top: 1.3rem;//20px
    }
  }


}
</style>
